import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "./layouts/Dashboard";
import LoginLayout from "./layouts/Login";
import AccountSettings from "./pages/Account/Settings";
import AccountSettingsPage from "./pages/Account/Settings/Settings";
import AccountInfoPage from "./pages/Account/Settings/UserInfo";
import NotificationPreferencesPage from "./pages/Account/Settings/NotificationPreferences";
import Clients from "./pages/Clients";
import ClientOnboarding from "./pages/Clients/Onboard";
import ClientShow from "./pages/Clients/Show";
import ClientEdit from "./pages/Clients/Edit";
import Dashboard from "./pages/Dashboard";
import DashboardV2 from "./pages/DashboardV2";
import Drivers from "./pages/Drivers";
import DriverShow from "./pages/Drivers/Show";
import Help from "./pages/Help";
import GettingStarted from "./pages/Help/GettingStarted";
import FirstSteps from "./pages/Help/GettingStarted/FirstSteps";
import UserGuide from "./pages/Help/GettingStarted/UserGuide";
import BestPractices from "./pages/Help/GettingStarted/BestPractices";
import Jobs from "./pages/Jobs";
import JobWorkspaceIndex from "./pages/Jobs/v2/JobIndex";
import JobWorkspacePage from "./pages/Jobs/v2";
import CSVHandler from "./pages/SuperAdmin/CSVhandler";
import ReportsPage from "./pages/Reports";
// import JobsCreate from "./pages/Jobs/Create";
import JobsShow from "./pages/Jobs/Show";
import { Users, UserShow } from "./pages/Settings/Users";
import UserEdit from "./pages/Users/Edit";
import UserCreate from "./pages/Users/Create";
// import UserShow from "./pages/Users/Show";
import Login from "./pages/Login";
import MfaChallenge from "./pages/Login/MfaChallenge";
import NoMatch from "./pages/NoMatch";
import SuperAdmin from "./pages/SuperAdmin";
import Employees from "./pages/Employees";
// import CustomerOnboarding from "./pages/SuperAdmin/Customer/Onboarding";
// import Components from "./pages/Components";
import ForgotPassword from "./pages/ForgotPassword";
import WebLayout from "./layouts/WebLayout";
import ReportBug from "./pages/ReportBug";
import Invoices from "./pages/Invoices";
import InvoiceCreate from "./pages/Invoices/Create";
import InvoiceShow from "./pages/Invoices/Show";
import InvoiceEdit from "./pages/Invoices/Edit";
import InvoicesV2 from "./pages/InvoicesV2";
import InvoiceV2Create from "./pages/InvoicesV2/Create";
import InvoiceV2Edit from "./pages/InvoicesV2/Edit";
import InvoiceV2Show from "./pages/InvoicesV2/Show";
import Payouts from "./pages/Payouts";
import PayoutCreate from "./pages/Payouts/Create";
import PayoutsShow from "./pages/Payouts/Show";
import Customers from "./pages/SuperAdmin/Customers";
import Leads from "./pages/SuperAdmin/Leads";
import LeadShow from "./pages/SuperAdmin/Leads/Show";
import Feedback from "./pages/Feedback";
import PayoutEdit from "./pages/Payouts/Edit";
import UsersAdmin from "./pages/SuperAdmin/Users";
import ChangePassword from "./pages/ChangePassword";
import {
  fetchCurrentUser,
  selectCurrentUser,
} from "./redux/reducers/accountSlice";
// import AdminFeedback from "./pages/SuperAdmin/Feedback";
import FeedbackShow from "./pages/Feedback/Show";
import ClientLocationsIndex from "./pages/Locations";
import Colors from "./pages/Colors";
import OrganizationShow from "./pages/Organizations/Show";
import useVersionCheck from "./hooks/useVersionUpdate";
import Settings from "./pages/Settings";
import FeedbackCreate from "./pages/Feedback/Create";
import Manifests from "./pages/Manifests";
import ManifestsShow from "./pages/Manifests/Show";
import ManifestsEdit from "./pages/Manifests/Edit";
import ManifestsV2 from "./pages/ManifestsV2";
import ManifestV2Create from "./pages/ManifestsV2/Create";
import ManifestV2Show from "./pages/ManifestsV2/Show";
import { Onboarding } from "./pages/Onboarding";
import GeneralSettings from "./pages/Settings/GeneralSettings";
import Rates from "./pages/Settings/Rates";
import CustomerShow from "./pages/SuperAdmin/Customers/Show";
import FeatureFlagsPage from "./pages/SuperAdmin/FeatureFlags";
import { Billing } from "./pages/Settings/Billing";
import { Features } from "./pages/Settings/Features";
import { Scheduler } from "./shared/Scheduler";
import { useAuth, useConfig } from "./hooks";
import { Dispatch } from "./pages/Dispatch";
import Equipment from "./pages/Equipment";
import { EquipmentShow } from "./pages/Equipment/Show";
import EmployeeShow from "./pages/Employees/Show";
import InventoryShow from "./pages/Inventory/Show";
import Inventory from "./pages/Inventory";
import { PageLoading } from "./shared";
import Templates from "./pages/Settings/Templates";
import PDFTemplatesSettings from "./pages/Settings/PDFTemplates";
import TaskTemplates from "./pages/Settings/TaskTemplates"; // Temporarily disabled until task template backend is ready
import NotificationTemplates from "./pages/Settings/NotificationTemplates";
import Reports from "./pages/Settings/Reports";
import TagsSettings from "./pages/Settings/Tags";
import Approval from "./pages/Approval";
import Performance from "./pages/SuperAdmin/Performance";
import AppHealthPage from "./pages/SuperAdmin/Health";
import AuditLogsPage from "./pages/SuperAdmin/AuditLogs";
import PUBLIC_ROUTES from "./constants/public.routes";
import EmailSettings from "./pages/Settings/EmailSettings";
import { RouteErrorBoundary } from "./shared/ErrorBoundary/RouteErrorBoundary";
import { PerformanceMonitor } from "./shared/PerformanceMonitor";
import DispatchSettings from "./pages/Settings/Dispatch";
import FleetSettings from "./pages/Settings/Fleet";
import DriversSettings from "./pages/Settings/Drivers";
import MotiveIntegrationsSettings from "./pages/Settings/Integrations/Motive";
import GoogleDriveSettings from "./pages/Settings/GoogleDrive";
import AdvancedSettings from "./pages/Settings/Advanced";
import RolesPermissions from "./pages/Settings/RolesPermissions";
import RequireFeature from "./components/RequireFeature";
import { JOBS_V2_FEATURE_KEY } from "./utils/featureFlags";
import RequirePermission from "./components/RequirePermission";
import RequireSuperAdmin from "./components/RequireSuperAdmin";
import DocsAccess from "./pages/DocsAccess";
import DocsAccessFrame from "./pages/DocsAccessFrame";
import { authReturnPath } from "./utils/authReturnPath";
import { PERMISSIONS } from "./constants/access-control";
// import { DispatchListPage } from "./pages/Dispatch/DispatchListPage";

const ProtectedRoute = ({ children }) => {
  const { user, initialized } = useAuth();
  const { loading: configLoading } = useConfig();
  const location = useLocation();

  if (!initialized || (user && configLoading)) {
    return <PageLoading />;
  }

  if (!user) {
    // Allow access to activation and password reset routes
    if (PUBLIC_ROUTES.includes(location.pathname)) {
      return children;
    }
    // Redirect to login with current location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return <PageLoading />;
  }

  if (user) {
    // Redirect to intended destination or home
    return <Navigate to={authReturnPath(location.state?.from)} replace />;
  }

  return children;
};

function App() {
  useVersionCheck();

  return (
    <>
      <PerformanceMonitor />
      <RouteErrorBoundary>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route
              path="dashboard-v2"
              element={
                <RequireFeature featureKey="dashboards.enabled">
                  <DashboardV2 />
                </RequireFeature>
              }
            />
            <Route
              path="jobs"
              element={
                <RequireFeature featureKey="jobs.enabled">
                  <RequirePermission permission={PERMISSIONS.JOBS_READ}>
                    <Jobs />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            {/* <Route path="jobs/create" element={<JobsCreate />} /> */}
            <Route
              path="jobs/v2"
              element={
                <RequireFeature featureKey="jobs.enabled">
                  <RequireFeature featureKey={JOBS_V2_FEATURE_KEY} optIn>
                    <RequirePermission permission={PERMISSIONS.JOBS_READ}>
                      <JobWorkspaceIndex />
                    </RequirePermission>
                  </RequireFeature>
                </RequireFeature>
              }
            />
            <Route
              path="jobs/v2/:id"
              element={
                <RequireFeature featureKey="jobs.enabled">
                  <RequireFeature featureKey={JOBS_V2_FEATURE_KEY} optIn>
                    <RequirePermission permission={PERMISSIONS.JOBS_READ}>
                      <JobWorkspacePage />
                    </RequirePermission>
                  </RequireFeature>
                </RequireFeature>
              }
            />
            <Route
              path="jobs/:id"
              element={
                <RequireFeature featureKey="jobs.enabled">
                  <RequirePermission permission={PERMISSIONS.JOBS_READ}>
                    <JobsShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route path="users" element={<Users />} />
            <Route path="users/create" element={<UserCreate />} />
            <Route path="users/:id" element={<UserEdit />} />
            <Route path="users/:id/edit" element={<UserEdit />} />
            {/* <Route path="account" element={<Account  />} /> */}
            {/* <Route path="account/settings" element={<AccountSettings />} /> */}
            <Route path="calendar" element={<Navigate to="/dispatch" replace />} />
            <Route
              path="dispatch"
              element={
                <RequireFeature featureKey="dispatch.enabled">
                  <RequirePermission permission={PERMISSIONS.DISPATCH_READ}>
                    <Dispatch />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="clients"
              element={
                <RequireFeature featureKey="clients.enabled">
                  <RequirePermission permission={PERMISSIONS.CLIENTS_READ}>
                    <Clients />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="clients/create"
              element={
                <RequireFeature featureKey="clients.enabled">
                  <RequirePermission permission={PERMISSIONS.CLIENTS_CREATE}>
                    <ClientOnboarding />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="clients/:id"
              element={
                <RequireFeature featureKey="clients.enabled">
                  <RequirePermission permission={PERMISSIONS.CLIENTS_READ}>
                    <ClientShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="clients/:id/locations"
              element={
                <RequireFeature featureKey="clients.enabled">
                  <RequirePermission permission={PERMISSIONS.CLIENTS_READ}>
                    <ClientLocationsIndex />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="clients/:id/edit"
              element={
                <RequireFeature featureKey="clients.enabled">
                  <RequirePermission permission={PERMISSIONS.CLIENTS_EDIT}>
                    <ClientEdit />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="drivers"
              element={
                <RequireFeature featureKey="drivers.enabled">
                  <RequirePermission permission={PERMISSIONS.DRIVERS_READ}>
                    <Drivers />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="drivers/:id/"
              element={
                <RequireFeature featureKey="drivers.enabled">
                  <RequirePermission permission={PERMISSIONS.DRIVERS_READ}>
                    <DriverShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route path="organizations/:id/" element={<OrganizationShow />} />
            <Route
              path="employees"
              element={
                <RequireFeature
                  featureKey="employees.enabled"
                  hideForAppTypes={["trucking"]}
                  fallbackPath="/drivers"
                >
                  <RequirePermission permission={PERMISSIONS.EMPLOYEES_READ}>
                    <Employees />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="employees/:id"
              element={
                <RequireFeature
                  featureKey="employees.enabled"
                  hideForAppTypes={["trucking"]}
                  fallbackPath="/drivers"
                >
                  <RequirePermission permission={PERMISSIONS.EMPLOYEES_READ}>
                    <EmployeeShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="manifests/v2"
              element={
                <RequireFeature featureKey="manifests.v2.enabled" optIn>
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_READ}>
                    <ManifestsV2 />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="manifests/v2/create"
              element={
                <RequireFeature featureKey="manifests.v2.enabled" optIn>
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_CREATE}>
                    <ManifestV2Create />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="manifests/v2/:id"
              element={
                <RequireFeature featureKey="manifests.v2.enabled" optIn>
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_READ}>
                    <ManifestV2Show />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="manifests"
              element={
                <RequireFeature featureKey="manifests.enabled">
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_READ}>
                    <Manifests />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="equipment"
              element={
                <RequirePermission permission={PERMISSIONS.EQUIPMENT_READ}>
                  <Equipment />
                </RequirePermission>
              }
            />
            <Route
              path="equipment/:id"
              element={
                <RequirePermission permission={PERMISSIONS.EQUIPMENT_READ}>
                  <EquipmentShow />
                </RequirePermission>
              }
            />
            {/* Inventory permissions are retired; routes are gated by the
                `inventory.enabled` feature flag only. */}
            <Route
              path="inventory"
              element={
                <RequireFeature featureKey="inventory.enabled">
                  <Inventory />
                </RequireFeature>
              }
            />
            <Route
              path="inventory/:id"
              element={
                <RequireFeature featureKey="inventory.enabled">
                  <InventoryShow />
                </RequireFeature>
              }
            />
            {/* <Route path="manifests/create" element={<Payouts />} /> */}
            <Route
              path="manifests/:id/edit"
              element={
                <RequireFeature featureKey="manifests.enabled">
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_EDIT}>
                    <ManifestsEdit />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="manifests/:id"
              element={
                <RequireFeature featureKey="manifests.enabled">
                  <RequirePermission permission={PERMISSIONS.MANIFESTS_READ}>
                    <ManifestsShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="payouts"
              element={
                <RequireFeature featureKey="payouts.enabled">
                  <RequirePermission permission={PERMISSIONS.PAYOUTS_READ}>
                    <Payouts />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="payouts/create"
              element={
                <RequireFeature featureKey="payouts.enabled">
                  <RequirePermission permission={PERMISSIONS.PAYOUTS_CREATE}>
                    <PayoutCreate />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="reports"
              element={
                <RequireFeature featureKey="reports.enabled">
                  <RequirePermission permission={PERMISSIONS.REPORTS_READ}>
                    <ReportsPage />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="payouts/:id/edit"
              element={
                <RequireFeature featureKey="payouts.enabled">
                  <RequirePermission permission={PERMISSIONS.PAYOUTS_EDIT}>
                    <PayoutEdit />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="payouts/:id"
              element={
                <RequireFeature featureKey="payouts.enabled">
                  <RequirePermission permission={PERMISSIONS.PAYOUTS_READ}>
                    <PayoutsShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="invoices"
              element={
                <RequireFeature featureKey="invoices.enabled">
                  <RequirePermission permission={PERMISSIONS.INVOICES_READ}>
                    <Invoices />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="invoices/v2"
              element={
                <RequirePermission permission={PERMISSIONS.INVOICES_READ}>
                  <InvoicesV2 />
                </RequirePermission>
              }
            />
            <Route
              path="invoices/v2/create"
              element={
                <RequirePermission permission={PERMISSIONS.INVOICES_CREATE}>
                  <InvoiceV2Create />
                </RequirePermission>
              }
            />
            <Route
              path="invoices/v2/:id/edit"
              element={
                <RequirePermission permission={PERMISSIONS.INVOICES_EDIT}>
                  <InvoiceV2Edit />
                </RequirePermission>
              }
            />
            <Route
              path="invoices/v2/:id"
              element={
                <RequirePermission permission={PERMISSIONS.INVOICES_READ}>
                  <InvoiceV2Show />
                </RequirePermission>
              }
            />
            <Route
              path="invoices/:id/edit"
              element={
                <RequireFeature featureKey="invoices.enabled">
                  <RequirePermission permission={PERMISSIONS.INVOICES_EDIT}>
                    <InvoiceEdit />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <RequireFeature featureKey="invoices.enabled">
                  <RequirePermission permission={PERMISSIONS.INVOICES_READ}>
                    <InvoiceShow />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route
              path="invoices/create"
              element={
                <RequireFeature featureKey="invoices.enabled">
                  <RequirePermission permission={PERMISSIONS.INVOICES_CREATE}>
                    <InvoiceCreate />
                  </RequirePermission>
                </RequireFeature>
              }
            />
            <Route path="feedback" element={<Feedback />} />
            <Route path="feedback/create" element={<FeedbackCreate />} />
            <Route path="feedback/:id" element={<FeedbackShow />} />
            <Route path="feedback/show" element={<FeedbackCreate />} />
            <Route
              path="settings"
              element={
                <RequireFeature featureKey="settings.enabled">
                  <Settings />
                </RequireFeature>
              }
            >
              <Route index element={<GeneralSettings />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route
                path="rates"
                element={
                  <RequireFeature featureKey="settings.rates.enabled">
                    <Rates />
                  </RequireFeature>
                }
              />
              <Route
                path="users"
                element={
                  <RequireFeature featureKey="settings.users.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_USERS_READ}
                    >
                      <Users />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="users/:id"
                element={
                  <RequireFeature featureKey="settings.users.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_USERS_READ}
                    >
                      <UserShow />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="roles-permissions"
                element={
                  <RequireFeature featureKey="settings.users.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_ROLES_READ}
                    >
                      <RolesPermissions />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="billing"
                element={
                  <RequireFeature featureKey="settings.billing.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_BILLING_READ}
                    >
                      <Billing />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="features"
                element={
                  <RequireFeature featureKey="settings.enabled">
                    <Features />
                  </RequireFeature>
                }
              />
              <Route
                path="templates"
                element={
                  <RequireFeature featureKey="settings.templates.enabled">
                    <Templates />
                  </RequireFeature>
                }
              />
              <Route
                path="pdf-templates"
                element={
                  <RequireFeature featureKey="settings.templates.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_TEMPLATES_MANAGE}
                    >
                      <PDFTemplatesSettings />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="task-templates"
                element={
                  <RequireFeature featureKey="settings.taskTemplates.enabled">
                    <TaskTemplates />
                  </RequireFeature>
                }
              />
              <Route
                path="tags"
                element={
                  <RequireFeature featureKey="settings.tags.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_TAGS_MANAGE}
                    >
                      <TagsSettings />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="notification-templates"
                element={
                  <RequireFeature featureKey="settings.notificationTemplates.enabled">
                    <NotificationTemplates />
                  </RequireFeature>
                }
              />
              <Route
                path="reports"
                element={
                  <RequireFeature
                    featureKey="settings.reports.enabled"
                    hideForAppTypes={["trucking"]}
                    fallbackPath="/settings"
                  >
                    <Reports />
                  </RequireFeature>
                }
              />
              <Route path="email" element={<EmailSettings />} />
              <Route
                path="google-drive"
                element={
                  <RequirePermission
                    permission={PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE}
                  >
                    <GoogleDriveSettings />
                  </RequirePermission>
                }
              />
              <Route
                path="dispatch"
                element={
                  <RequireFeature featureKey="settings.dispatch.enabled">
                    <DispatchSettings />
                  </RequireFeature>
                }
              />
              <Route path="fleet" element={<FleetSettings />} />
              <Route path="drivers" element={<DriversSettings />} />
              <Route
                path="integrations"
                element={
                  <RequireFeature
                    featureKey="integrations.motive.enabled"
                    optIn
                  >
                    <RequirePermission permission={PERMISSIONS.FLEET_VIEW}>
                      <MotiveIntegrationsSettings />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
              <Route
                path="advanced"
                element={
                  <RequireFeature featureKey="settings.advanced.enabled">
                    <RequirePermission
                      permission={PERMISSIONS.SETTINGS_ADVANCED_MANAGE}
                    >
                      <AdvancedSettings />
                    </RequirePermission>
                  </RequireFeature>
                }
              />
            </Route>
            <Route path="account" element={<AccountSettings />}>
              <Route index element={<AccountInfoPage />} />
              <Route path="information" element={<AccountInfoPage />} />
              <Route path="settings" element={<AccountSettingsPage />} />
              <Route
                path="notifications"
                element={<NotificationPreferencesPage />}
              />
            </Route>
            {/* <Route path="onboarding" element={<Onboarding />} /> */}
            <Route path="colors" element={<Colors />} />
            <Route path="help" element={<Help />} />
            <Route path="help/features" element={<GettingStarted />} />
            <Route path="help/features/first-steps" element={<FirstSteps />} />
            <Route path="help/features/user-guide" element={<UserGuide />} />
            <Route
              path="help/features/best-practices"
              element={<BestPractices />}
            />
          </Route>
          <Route
            path="/docs-access"
            element={
              <ProtectedRoute>
                <DocsAccess />
              </ProtectedRoute>
            }
          />
          <Route path="/docs-access-frame" element={<DocsAccessFrame />} />
          <Route
            path="/"
            element={
              <PublicRoute>
                <LoginLayout />
              </PublicRoute>
            }
          >
            <Route path="login" element={<Login />} />
            <Route path="login/mfa" element={<MfaChallenge />} />
            <Route path="forgot/password" element={<ForgotPassword />} />
            <Route path="change/password" element={<ChangePassword />} />
            <Route path="activate" element={<Onboarding />} />
          </Route>
          <Route
            path="admin"
            element={
              <ProtectedRoute>
                <RequireSuperAdmin>
                  <DashboardLayout />
                </RequireSuperAdmin>
              </ProtectedRoute>
            }
          >
            <Route index element={<SuperAdmin />} />
            <Route path="feedback/:id" element={<FeedbackShow />} />
            {/* <Route path="customers/onboarding" element={<CustomerOnboarding />} /> */}
            {/* <Route path="customers/:id" element={<CustomerOnboarding />} /> */}
            <Route path="csv-handler" element={<CSVHandler />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerShow />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="users/:id" element={<UserEdit />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="performance" element={<Performance />} />
            <Route path="health" element={<AppHealthPage />} />
            <Route path="feature-flags" element={<FeatureFlagsPage />} />
            <Route path="audit-logs" element={<AuditLogsPage />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadShow />} />
          </Route>
          <Route
            path="/"
            element={
              <PublicRoute>
                <WebLayout />
              </PublicRoute>
            }
          >
            <Route path="help" element={<Help />} />
            <Route path="report/bug" element={<ReportBug />} />
            <Route path="approve-quote" element={<Approval type="item" />} />
            <Route path="approve-proposal" element={<Approval type="job" />} />
            <Route path="*" element={<NoMatch />} />
          </Route>
        </Routes>
      </RouteErrorBoundary>
    </>
  );
}

export default App;
